/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { ThemeProvider, useTheme } from './theme/ThemeContext';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import DashboardBody from './pages/DashboardBody';
import Settings from './pages/Settings';
import SignInPage from './pages/SignInPage';
import ApplicationsListPage from './pages/ApplicationsListPage';
import ApplicationDetailsPage from './pages/ApplicationDetailsPage';
import ApplicationPackListPage from './pages/ApplicationPackListPage';
import MyPackagesListPage from './pages/MyPackagesListPage';
import ApllicationPackDetailsPage from './pages/ApllicationPackDetailsPage';
import ApplicationCreateEditForm from './components/ApplicationCreateEditForm';
import ApplicationSettingsForm from './components/ApplicationSettingsForm';
import { AlertDialogProvider } from './components/common/AlertDialogContext';
import TableCreateForm from './pages/TableCreateForm';
import PrivateRoute from './components/PrivateRouter';
import OrganizationRegistrationPage from './pages/OrganizationRegistrationPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import CreateEditRolePage from './pages/CreateEditRolePage';
import RoleListPage from './pages/RoleListPage';
import ApplicationUsersListPage from './pages/ApplicationUsersListPage';
import CreateApplicationUserPage from './pages/CreateApplicationUserPage';
import TablePermissionsListPage from './pages/TablePermissionsListPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UpdateApplicationUserPage from './pages/UpdateApplicationUserPage';
import UserProfilePage from './pages/UserProfilePage';
import EditTablePage from './pages/EditTablePage';
import OrganizationGroupTypesPage from './pages/OrganizationGroupTypesPage';
import OrganizationGroupsPage from './pages/OrganizationGroupsPage';
import NotificationAnalyticsPage from './pages/NotificationAnalyticsPage';
import PaymentAnalyticsPage from './pages/PaymentAnalyticsPage';
import CertificateTemplatesPage from './pages/CertificateTemplatesPage';
import CertificateTemplateFormPage from './pages/CertificateTemplateFormPage';
import PaymentTracePage from './pages/PaymentTracePage';
import PaymentDebuggingPage from './pages/PaymentDebuggingPage';
import NotificationControlsPage from './pages/NotificationControlsPage';
import EventHandlingPage from './pages/EventHandlingPage';
import WebhookEndpointsPage from './pages/WebhookEndpointsPage';
import PublicPagesHub from './pages/PublicPagesHub';
import PublicViewConfigPage from './pages/PublicViewConfigPage';
import PageBuilderHubPage from './pages/PageBuilderHubPage';
import PageBuilderEditorPage from './pages/PageBuilderEditorPage';
import FormSubmissionsPage from './pages/FormSubmissionsPage';
import OwnershipRelationsPage from './pages/OwnershipRelationsPage';
import EventCreatePage from './pages/EventCreatePage';
import EventListPage from './pages/EventListPage';
import OrganizationListPage from './pages/OrganizationListPage';
import OrganizationFormPage from './pages/OrganizationFormPage';

function ThemeSwitcher() {
  const { theme, switchTheme } = useTheme();
  return (
    <button
      className="btn btn-outline-primary"
      style={{ margin: 12 }}
      onClick={switchTheme}
    >
      Switch to {theme.name === 'bhairava' ? 'rudra' : 'bhairava'} Theme
    </button>
  );
}

function App() {
  return (
    <AlertDialogProvider>
      {/* Optional: Add ThemeSwitcher globally */}
      {/* <ThemeSwitcher /> */}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<OrganizationRegistrationPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }>
          <Route path="" element={<DashboardBody />} />
          <Route path="settings" element={<Settings />} />
          <Route path="marketplace" element={<ApplicationPackListPage />} />
          <Route path="marketplace/:id" element={<ApllicationPackDetailsPage />} />
          <Route path="my-packages" element={<MyPackagesListPage />} />
          <Route path="my-packages/:id" element={<ApllicationPackDetailsPage />} />
          <Route path="applications" element={<ApplicationsListPage />} />
          <Route path="applications/:id" element={<ApplicationDetailsPage />} />
          <Route path="applications/create" element={<ApplicationCreateEditForm />} />
          <Route path="applications/:id/update" element={<ApplicationCreateEditForm />} />
          <Route path="applications/:id/settings" element={<ApplicationSettingsForm />} />
          <Route path="applications/:id/table_create" element={<TableCreateForm />} />
          <Route path="applications/:id/:tableName/edit" element={<EditTablePage />} />
          <Route path="applications/:id/users" element={<ApplicationUsersListPage />} />
          <Route path="applications/:id/users/create" element={<CreateApplicationUserPage />} />
          <Route path="applications/:id/users/:userId/update" element={<UpdateApplicationUserPage />} />
          <Route path="applications/:id/users/:userId/permissions" element={<TablePermissionsListPage />} />
          <Route path="applications/:id/roles" element={<RoleListPage />} />
          <Route path="applications/:id/roles/:roleId/update" element={<CreateEditRolePage />} />
          <Route path="applications/:id/roles/create" element={<CreateEditRolePage />} />
          <Route path="applications/:id/group-types" element={<OrganizationGroupTypesPage />} />
          <Route path="applications/:id/group-types/groups" element={<OrganizationGroupsPage />} />
          <Route path="applications/:id/groups" element={<OrganizationGroupsPage />} />
          <Route path="applications/:id/groups/group-types" element={<OrganizationGroupTypesPage />} />
          <Route path="applications/:id/groups/:groupId/ownership-relations" element={<OwnershipRelationsPage />} />
          <Route path="applications/:id/payment-analytics" element={<PaymentAnalyticsPage />} />
          <Route path="applications/:id/certificate-templates" element={<CertificateTemplatesPage />} />
          <Route path="applications/:id/certificate-templates/new" element={<CertificateTemplateFormPage />} />
          <Route path="applications/:id/certificate-templates/:templateId/edit" element={<CertificateTemplateFormPage />} />
          <Route path="applications/:id/payment-trace" element={<PaymentTracePage />} />
          <Route path="applications/:id/payment-debugging" element={<PaymentDebuggingPage />} />
          <Route path="applications/:id/notification-analytics" element={<NotificationAnalyticsPage />} />
          <Route path="applications/:id/notification-analytics/notification-controls" element={<NotificationControlsPage />} />
          <Route path="applications/:id/notification-controls" element={<NotificationControlsPage />} />
          <Route path="applications/:id/notification-controls/notification-analytics" element={<NotificationAnalyticsPage />} />
          <Route path="applications/:id/events" element={<EventHandlingPage />} />
          <Route path="applications/:id/athlohive/events" element={<EventListPage />} />
          <Route path="applications/:id/athlohive/organizations" element={<OrganizationListPage />} />
          <Route path="applications/:id/athlohive/organizations/create" element={<OrganizationFormPage />} />
          <Route path="applications/:id/athlohive/organizations/:organizationRecordId/edit" element={<OrganizationFormPage />} />
          <Route path="applications/:id/athlohive/events/create" element={<EventCreatePage />} />
          <Route path="applications/:id/athlohive/events/:eventId/edit" element={<EventCreatePage />} />
          <Route path="applications/:id/webhooks" element={<WebhookEndpointsPage />} />
          <Route path="applications/:id/public-pages" element={<PublicPagesHub />} />
          <Route path="applications/:id/public-pages/:tableId/configure" element={<PublicViewConfigPage />} />
          <Route path="applications/:id/page-builder" element={<PageBuilderHubPage />} />
          <Route path="applications/:id/page-builder/:pageId/edit" element={<PageBuilderEditorPage />} />
          <Route path="applications/:id/page-builder/:pageId/forms/:formId/submissions" element={<FormSubmissionsPage />} />
          <Route path="profile" element={<UserProfilePage />} />
        </Route>
      </Routes>
    </AlertDialogProvider>
  );
}

export default App;
